import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { theme } from '../../theme';
import { useApi } from '../../hooks/useApi';

export function SettingsScreen({ onNavigate }) {
  const { user } = useAuth();
  const api = useApi();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', bio: '' });

  useEffect(() => {
    let cancelled = false;
    async function fetchProfile() {
      try {
        const res = await api.auth.me();
        const data = res.data || res;
        if (!cancelled) {
          setProfile(data);
          if (data.maker) {
            setForm({
              name: data.maker.name || '',
              phone: data.maker.phone || '',
              bio: data.maker.bio || '',
            });
          }
        }
      } catch (err) {
        console.error('Profile fetch error:', err);
      }
    }
    fetchProfile();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    if (!profile?.maker?.id) return;
    setSaving(true);
    try {
      await api.makers.update(profile.maker.id, {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        bio: form.bio.trim() || null,
      });
      setEditing(false);
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <button
        onClick={() => onNavigate('my-page')}
        style={{ background: 'none', border: 'none', color: theme.accent, cursor: 'pointer', marginBottom: '16px' }}
      >
        ← Til baka
      </button>

      <h2 style={{ fontSize: '18px', fontWeight: 700, color: theme.text, marginBottom: '20px' }}>
        Stillingar
      </h2>

      {/* Google Profile (read-only) */}
      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: theme.radius,
        padding: '16px',
        marginBottom: '16px',
      }}>
        <h3 style={{ fontSize: '14px', fontWeight: 600, color: theme.textSecondary, marginBottom: '12px' }}>
          Reikningur
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img
            src={user?.picture}
            alt=""
            style={{ width: '40px', height: '40px', borderRadius: '50%' }}
          />
          <div>
            <p style={{ fontSize: '14px', fontWeight: 600, color: theme.text }}>{user?.name}</p>
            <p style={{ fontSize: '12px', color: theme.textSecondary }}>{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Maker Profile (editable) */}
      {profile?.maker && (
        <div style={{
          background: theme.bgCard,
          border: `1px solid ${theme.border}`,
          borderRadius: theme.radius,
          padding: '16px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, color: theme.textSecondary }}>
              Maker prófíll
            </h3>
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                style={{
                  background: theme.accentBg,
                  border: `1px solid ${theme.accent}`,
                  borderRadius: theme.radiusSm,
                  padding: '6px 12px',
                  color: theme.accent,
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Breyta
              </button>
            )}
          </div>

          {editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Nafn</label>
                <input
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Sími</label>
                <input
                  value={form.phone}
                  onChange={e => setForm(prev => ({ ...prev, phone: e.target.value }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Um mig</label>
                <textarea
                  value={form.bio}
                  onChange={e => setForm(prev => ({ ...prev, bio: e.target.value }))}
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setEditing(false)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: theme.bgCard,
                    border: `1px solid ${theme.border}`,
                    borderRadius: theme.radiusSm,
                    color: theme.text,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Hætta við
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: theme.accent,
                    color: '#000',
                    border: 'none',
                    borderRadius: theme.radiusSm,
                    fontWeight: 600,
                    cursor: saving ? 'wait' : 'pointer',
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  {saving ? 'Vista...' : 'Vista'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={infoRow}>
                <span style={infoLabel}>Nafn</span>
                <span style={infoValue}>{profile.maker.name}</span>
              </div>
              <div style={infoRow}>
                <span style={infoLabel}>Sími</span>
                <span style={infoValue}>{profile.maker.phone || '—'}</span>
              </div>
              <div style={infoRow}>
                <span style={infoLabel}>Staðsetning</span>
                <span style={infoValue}>{profile.maker.location || '—'}</span>
              </div>
              <div style={infoRow}>
                <span style={infoLabel}>Einkunn</span>
                <span style={infoValue}>
                  {'★'.repeat(Math.round(profile.maker.rating || 0))} {(profile.maker.rating || 0).toFixed(1)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const labelStyle = {
  fontSize: '13px',
  fontWeight: 600,
  color: theme.textSecondary,
  display: 'block',
  marginBottom: '4px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  background: theme.bgInput,
  border: `1px solid ${theme.border}`,
  borderRadius: theme.radiusSm,
  color: theme.text,
  fontSize: '14px',
  outline: 'none',
};

const infoRow = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '6px 0',
};

const infoLabel = {
  fontSize: '13px',
  color: theme.textSecondary,
};

const infoValue = {
  fontSize: '14px',
  color: theme.text,
  fontWeight: 500,
};
